/**
 * ScriptVersionSelector.jsx
 *
 * Phase 4a — BOOK_VOICE_STATS_SPEC §7.
 *
 * Lets the Book Creator pick which `processedVersionItems` entry (JSON-map
 * §2.2) is the active render target for a book. The selected version drives
 * the <BookVoiceStatsStrip /> Coverage cell and the <VoiceCloningPipeline />
 * Coverage Panel via `fetchBookScriptCoverage({ bookId, scriptVersion })`.
 *
 * Design notes:
 *   - Source of versions: `state.user.storyText.fullBookDataSet.processedVersionItems`
 *     (shipped by `storyEditorSlice.fetchFullBookDataSet`). **Do not re-fetch**
 *     — the worksheet / pipeline pages already dispatch that thunk (§7.2).
 *   - Default selection is the highest `parseInt(version, 10)` (JSON-map §2.2
 *     "latest rule") — a small "latest" pill annotates it.
 *   - On change, dispatches `setActiveScriptVersion({ bookId, scriptVersion })`
 *     so the slice records the pick and flushes cached coverage (§7.4).
 *   - Native <select> per §7.5 — no custom combobox, matches the rest of the
 *     worksheet UI density.
 *   - If the book has no imported scripts yet, renders a disabled placeholder
 *     reading "No imported script versions yet."
 */

import React, { useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import {
  setActiveScriptVersion,
  selectActiveScriptVersion
} from '../../slices/voiceStatsSlice';

import './ScriptVersionSelector.css';

/** Parse a version-ish value into a sortable integer. Non-numeric → -1 so
 *  those entries sort to the bottom without crashing. */
const versionInt = (v) => {
  const n = parseInt(v, 10);
  return Number.isFinite(n) ? n : -1;
};

/** Shape an entry for the <select>. Supports two field shapes seen in the
 *  wild (`version` vs `versionNumber`) so we don't assume either. */
const normalizeItem = (raw) => {
  if (!raw) return null;
  const version =
    raw.version != null
      ? String(raw.version)
      : raw.versionNumber != null
        ? String(raw.versionNumber)
        : null;
  const fileName = raw.fileName || raw.filename || raw.name || null;
  const uuid = raw.uuid || raw.id || null;
  if (!uuid && !fileName) return null;
  return { uuid, fileName, version, thumbnail: raw.thumbnail || null };
};

const truncate = (s, n = 28) =>
  !s ? '' : s.length <= n ? s : `${s.slice(0, n - 1)}…`;

const ScriptVersionSelector = ({
  bookId,
  value,
  onChange,
  showLatestBadge = true,
  dense = false,
  className = ''
}) => {
  const dispatch = useDispatch();

  // Pull processedVersionItems out of storyEditorSlice. Worksheet page is
  // expected to have already dispatched fetchFullBookDataSet, so in the
  // common case this is an already-populated array.
  const rawItems = useSelector(
    (s) => s.user?.storyText?.fullBookDataSet?.processedVersionItems || []
  );
  const active = useSelector((s) => selectActiveScriptVersion(s, bookId));

  // Normalize + sort once per change.
  const items = useMemo(() => {
    const norm = (Array.isArray(rawItems) ? rawItems : [])
      .map(normalizeItem)
      .filter(Boolean);
    norm.sort((a, b) => versionInt(b.version) - versionInt(a.version));
    return norm;
  }, [rawItems]);

  const latest = items[0] || null;

  // Figure out which entry is currently selected. Preference order:
  //   1. explicit prop `value`
  //   2. slice's `active.uuid` (user's prior pick on this book)
  //   3. latest
  const selected = useMemo(() => {
    if (!items.length) return null;
    const match = (needle) =>
      items.find((i) => i.uuid === needle) ||
      items.find((i) => i.version === String(needle)) ||
      null;
    return (
      (value && match(value)) ||
      (active?.uuid && match(active.uuid)) ||
      latest
    );
  }, [items, value, active, latest]);

  // When there's no active pick in the slice yet, seed it with the latest so
  // downstream components (the coverage thunk guard) have a stable version to
  // key on. One-shot: fires only on mount / when items arrive.
  useEffect(() => {
    if (!bookId) return;
    if (!selected) return;
    if (!active) {
      dispatch(
        setActiveScriptVersion({
          bookId,
          scriptVersion: selected
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookId, selected?.uuid]);

  const handleChange = (e) => {
    const uuid = e.target.value;
    const picked = items.find((i) => i.uuid === uuid) || null;
    if (!picked) return;
    dispatch(setActiveScriptVersion({ bookId, scriptVersion: picked }));
    if (onChange) onChange(picked);
  };

  // --- render ---
  if (!items.length) {
    return (
      <div
        className={`svs-wrap svs-empty ${dense ? 'svs-dense' : ''} ${className}`}
        role="status"
        aria-live="polite"
      >
        <span className="svs-label">Script</span>
        <span className="svs-empty-text">No imported script versions yet.</span>
      </div>
    );
  }

  const selectedIsLatest = selected && latest && selected.uuid === latest.uuid;

  return (
    <div className={`svs-wrap ${dense ? 'svs-dense' : ''} ${className}`}>
      <label className="svs-label" htmlFor={`svs-${bookId || 'anon'}`}>
        Script
      </label>
      <div className="svs-control">
        <select
          id={`svs-${bookId || 'anon'}`}
          className="svs-select"
          value={selected?.uuid || ''}
          onChange={handleChange}
          aria-label="Script version"
        >
          {items.map((item) => (
            <option key={item.uuid || item.fileName} value={item.uuid || ''}>
              {`v${item.version || '?'} · ${truncate(item.fileName || item.uuid)}`}
            </option>
          ))}
        </select>
        {showLatestBadge && selectedIsLatest && (
          <span className="svs-pill" aria-label="latest version">
            latest
          </span>
        )}
      </div>
    </div>
  );
};

ScriptVersionSelector.propTypes = {
  bookId: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func,
  showLatestBadge: PropTypes.bool,
  dense: PropTypes.bool,
  className: PropTypes.string
};

export default ScriptVersionSelector;
